/**
 * Schémas Zod pour le module YouTube Studio.
 *
 * Utilisés aux frontières :
 *   - validation des forms côté apps/app (éditeur de vidéo)
 *   - validation de l'input de l'edge function `youtube-enqueue`
 *   - typage partagé avec apps/video-worker (capture + composition FFmpeg)
 *
 * Les enums et bornes reflètent les CHECK / enums SQL de la migration
 * 0107_youtube_studio.sql. Toute modification doit être faite des deux côtés.
 */

import { z } from 'zod';

// ── Enums (miroir SQL) ───────────────────────────────────────────────────────

export const YoutubeVideoStatusSchema = z.enum([
  'draft',
  'queued',
  'rendering',
  'encoding',
  'uploading',
  'published',
  'failed',
]);
export type YoutubeVideoStatus = z.infer<typeof YoutubeVideoStatusSchema>;

export const YoutubeJobStatusSchema = z.enum([
  'queued',
  'processing',
  'done',
  'failed',
  'abandoned',
  'cancelled',
]);
export type YoutubeJobStatus = z.infer<typeof YoutubeJobStatusSchema>;

export const YoutubeJobStepSchema = z.enum([
  'capturing',
  'composing',
  'thumbnailing',
  'uploading',
  'finalizing',
]);
export type YoutubeJobStep = z.infer<typeof YoutubeJobStepSchema>;

export const SocialPlatformSchema = z.enum([
  'youtube',
  'tiktok',
  'instagram_reels',
  'facebook_reels',
  // Réseaux du blog (publication feed/page, migration 0131).
  'linkedin',
  'facebook',
  'instagram',
]);
export type SocialPlatform = z.infer<typeof SocialPlatformSchema>;

export const YoutubeAssetKindSchema = z.enum(['background_image', 'background_video', 'music']);
export type YoutubeAssetKind = z.infer<typeof YoutubeAssetKindSchema>;

export const SocialPrivacySchema = z.enum(['private', 'unlisted', 'public']);
export type SocialPrivacy = z.infer<typeof SocialPrivacySchema>;

export const AspectRatioSchema = z.enum(['landscape', 'portrait']);
export type AspectRatio = z.infer<typeof AspectRatioSchema>;

/**
 * Paliers de durée produit (s). Reflète le CHECK SQL
 * `duration_seconds in (30, 60, 300, 900, 1800, 3600, 7200, 28800)`.
 */
export const DURATION_OPTIONS = [
  { value: 30, label: '30 secondes' },
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 900, label: '15 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 heure' },
  { value: 7200, label: '2 heures' },
  { value: 28800, label: '8 heures' },
] as const;

export const DurationSecondsSchema = z.union([
  z.literal(30),
  z.literal(60),
  z.literal(300),
  z.literal(900),
  z.literal(1800),
  z.literal(3600),
  z.literal(7200),
  z.literal(28800),
]);
export type DurationSeconds = z.infer<typeof DurationSecondsSchema>;

// ── Overlay ──────────────────────────────────────────────────────────────────
// Stocké en jsonb sur youtube_videos.overlay. Compose le rendu FFmpeg et la
// prévisualisation DOM (apps/app/.../overlay-editor.tsx) — même source de vérité.

export const VideoOverlaySchema = z.object({
  title: z.string().max(120).default(''),
  subtitle: z.string().max(160).default(''),
  level: z.string().max(40).default(''),
  subject: z.string().max(60).default(''),
  show_logo: z.boolean().default(true),
  show_qr: z.boolean().default(true),
  show_url: z.boolean().default(true),
  /** URL encodée dans le QR + affichée en bas. */
  qr_url: z.string().url().default('https://jeraisonne.app'),
});
export type VideoOverlay = z.infer<typeof VideoOverlaySchema>;

export const DEFAULT_OVERLAY: VideoOverlay = VideoOverlaySchema.parse({});

// ── Spec de rendu (création / édition d'une vidéo) ───────────────────────────

/**
 * Un élément de la playlist d'une vidéo (table `youtube_video_items`). Un
 * exercice source + l'attempt rejoué (NULL = le worker résout le meilleur
 * attempt `completed`).
 */
export const VideoPlaylistItemSchema = z.object({
  exercise_id: z.string().uuid(),
  attempt_id: z.string().uuid().nullable().optional(),
});
export type VideoPlaylistItem = z.infer<typeof VideoPlaylistItemSchema>;

export const VideoSpecSchema = z.object({
  exercise_id: z.string().uuid(),
  attempt_id: z.string().uuid().nullable().optional(),
  background_asset_id: z.string().uuid().nullable().optional(),
  music_asset_id: z.string().uuid().nullable().optional(),
  aspect_ratio: AspectRatioSchema.default('landscape'),
  duration_seconds: DurationSecondsSchema.default(60),
  loop_background: z.boolean().default(true),
  overlay: VideoOverlaySchema.default(DEFAULT_OVERLAY),
  yt_title: z.string().max(100).nullable().optional(),
  yt_description: z.string().max(5000).nullable().optional(),
  yt_tags: z.array(z.string().max(60)).max(50).default([]),
  /**
   * Légende du post réseaux sociaux (Instagram Reels…). NULL = le worker
   * reconstruit une légende par défaut via `buildReelsCaption`. 2200 = limite
   * caption Instagram. Distincte de l'overlay dessiné dans la vidéo.
   */
  social_caption: z.string().max(2200).nullable().optional(),
  /**
   * Playlist ordonnée (≥ 1). `items[0]` = item primaire = `exercise_id`/`attempt_id`
   * de la ligne `youtube_videos`. Persistée dans `youtube_video_items`.
   */
  items: z.array(VideoPlaylistItemSchema).min(1),
});
export type VideoSpec = z.infer<typeof VideoSpecSchema>;

// ── Input edge function `youtube-enqueue` ────────────────────────────────────

export const EnqueueRenderSchema = z.object({
  video_id: z.string().uuid(),
  /** Plateformes cibles (au moins YouTube). Extensible TikTok/Reels. */
  platforms: z.array(SocialPlatformSchema).min(1).default(['youtube']),
  privacy: SocialPrivacySchema.default('private'),
  /** ISO 8601. NULL = publier immédiatement. */
  scheduled_at: z.string().datetime().nullable().optional(),
});
export type EnqueueRenderInput = z.infer<typeof EnqueueRenderSchema>;

// ── Métadonnées musique (crédit licence) ─────────────────────────────────────

export const MusicCreditSchema = z.object({
  music_artist: z.string().max(200).nullable().optional(),
  music_license: z.string().max(200).nullable().optional(),
  music_source_url: z.string().url().nullable().optional(),
});
export type MusicCredit = z.infer<typeof MusicCreditSchema>;
